import React, { useState, useMemo } from 'react';
import {
  Paper,
  Button,
  IconButton,
  Tooltip,
  Box,
  Typography,
  Chip,
  Switch,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
} from '@mui/material';
import { Search } from 'react-feather';
import { enqueueSnackbar } from 'notistack';
import {
  DataGridPro,
  GridColDef,
  GridRenderCellParams,
} from '@mui/x-data-grid-pro';

import { Trash2, Edit, Plus, X } from 'react-feather';
import { useQuery, useMutation } from '@apollo/client';
import moment from 'moment';
import { FullScreen } from '@pv/common/components';
import {
  FraudTokensQuery,
  FraudTokensQueryVariables,
  UpdateFraudTokenMutation,
  UpdateFraudTokenMutationVariables,
  DeleteFraudTokenMutation,
  DeleteFraudTokenMutationVariables,
} from '@pv/common/graphql/operations';
import { FraudTokenTypeEnum } from '@pv/common/graphql/types';

import {
  FRAUD_TOKENS_QUERY,
  UPDATE_FRAUD_TOKEN_MUTATION,
  DELETE_FRAUD_TOKEN_MUTATION,
} from './graphql';
import { FraudTokenDetailsDialog } from './FraudTokenDetailsDialog';
import { EditFraudTokenDialog } from './EditFraudTokenDialog';
import { AddFraudTokenDialog } from './AddFraudTokenDialog';
import { reportSx } from './styles';

type FraudToken = FraudTokensQuery['fraudTokens'][number];

export const FraudTokensTable = () => {
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editingToken, setEditingToken] = useState<FraudToken | null>(null);
  const [activeTab, setActiveTab] = useState<
    'all' | FraudTokenTypeEnum | 'suspicious_reports'
  >('all');
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedTokenId, setSelectedTokenId] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState('');

  const { data, loading, refetch } = useQuery<
    FraudTokensQuery,
    FraudTokensQueryVariables
  >(FRAUD_TOKENS_QUERY, {
    variables: {
      tokenType:
        activeTab === 'all' || activeTab === 'suspicious_reports'
          ? undefined
          : activeTab,
    },
    skip: activeTab === 'suspicious_reports',
  });
  const [updateFraudToken] = useMutation<
    UpdateFraudTokenMutation,
    UpdateFraudTokenMutationVariables
  >(UPDATE_FRAUD_TOKEN_MUTATION);

  const [deleteFraudToken] = useMutation<
    DeleteFraudTokenMutation,
    DeleteFraudTokenMutationVariables
  >(DELETE_FRAUD_TOKEN_MUTATION, {
    update: (cache, { data: mutationResult }) => {
      if (!mutationResult?.deleteFraudToken?.fraudToken) return;
      const normalizedId = cache.identify({
        id: mutationResult.deleteFraudToken.fraudToken.id,
        __typename: 'FraudToken',
      });
      cache.evict({ id: normalizedId });
      cache.gc();
    },
    onError: (error) => {
      enqueueSnackbar(error.message, { variant: 'error' });
    },
  });

  const handleOpenEditDialog = (token?: FraudToken) => {
    setEditingToken(token || null);
  };

  const handleCloseEditDialog = () => {
    setEditingToken(null);
    refetch();
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this fraud token?')) {
      try {
        await deleteFraudToken({ variables: { id } });
      } catch (error) {
        console.error('Error deleting fraud token:', error);
      }
    }
  };

  const handleToggleActive = async (token: FraudToken) => {
    try {
      await updateFraudToken({
        variables: {
          id: token.id,
          active: !token.active,
        },
      });
      refetch();
    } catch (error) {
      console.error('Error toggling fraud token:', error);
    }
  };

  // Filter tokens based on search value
  const filteredTokens = useMemo(() => {
    if (!searchValue) return data?.fraudTokens || [];

    return (data?.fraudTokens || []).filter((token: FraudToken) => {
      const searchLower = searchValue.toLowerCase();
      return (
        token.value.toLowerCase().includes(searchLower) ||
        token.notes?.toLowerCase().includes(searchLower)
      );
    });
  }, [data?.fraudTokens, searchValue]);

  const columns: GridColDef[] = [
    {
      field: 'tokenType',
      headerName: 'Type',
      width: 100,
      renderCell: (params: GridRenderCellParams) => {
        const typeLabels = {
          email: 'Email',
          phone: 'Phone',
          message_text: 'Message',
        };
        return (
          <Chip
            label={
              typeLabels[params.value as FraudTokenTypeEnum] || params.value
            }
            size="small"
            color="primary"
            variant="outlined"
          />
        );
      },
    },
    {
      field: 'value',
      headerName: 'Value',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'fraudDetectionsCount',
      headerName: 'Hits',
      width: 100,
      valueFormatter: (params) => params.value || 0,
    },
    {
      field: 'confirmedFraudCount',
      headerName: 'Correct Detections',
      width: 100,
      valueFormatter: (params) => params.value || 0,
    },
    {
      field: 'falsePositiveCount',
      headerName: 'False Positives',
      width: 100,
      valueFormatter: (params) => params.value || 0,
    },
    {
      field: 'unknownCount',
      headerName: 'Ignored Warnings',
      width: 100,
      valueFormatter: (params) => params.value || 0,
    },
    {
      field: 'viewDetails',
      headerName: 'View Details',
      flex: 1,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Button
          onClick={(e) => {
            e.stopPropagation();
            setSelectedTokenId(params.row.id);
            setDetailsDialogOpen(true);
          }}
          variant="outlined"
        >
          View Details
        </Button>
      ),
    },
    {
      field: 'active',
      headerName: 'Status',
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={params.value ? 'Active' : 'Inactive'}
            size="small"
            color={params.value ? 'success' : 'default'}
          />
          <Switch
            checked={params.value}
            onChange={() => handleToggleActive(params.row)}
            color="success"
            size="small"
          />
        </Box>
      ),
    },
    {
      field: 'notes',
      headerName: 'Notes',
      flex: 2,
      minWidth: 300,
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 120,
      valueFormatter: (params) =>
        params.value ? moment(params.value).format('MM/DD/YYYY') : '-',
    },
    {
      field: 'createdBy',
      headerName: 'Created By',
      flex: 1,
      minWidth: 150,
      valueGetter: (params) => params.value?.email || '-',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          <Tooltip title="Edit">
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleOpenEditDialog(params.row);
              }}
            >
              <Edit size={16} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(params.row.id);
              }}
            >
              <Trash2 size={16} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <>
      <FullScreen paddingBottom={24}>
        {({ height }) => (
          <Paper sx={{ height, width: '100%', p: 2 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
              }}
            >
              <Typography variant="h5">Fraud Scanner</Typography>
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  alignItems: 'center',
                }}
              >
                <TextField
                  value={searchValue}
                  label="Search"
                  onChange={(e) => setSearchValue(e.target.value)}
                  size="small"
                  sx={{ maxWidth: 400, flex: 1 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search size={18} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setSearchValue('')}>
                          <X size={18} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Box>
                  <Button
                    variant="contained"
                    startIcon={<Plus size={16} />}
                    onClick={() => setAddDialogOpen(true)}
                  >
                    Add To Fraud Scanner
                  </Button>
                </Box>
              </Box>
            </Box>
            <Tabs
              value={activeTab}
              onChange={(_, newValue) => setActiveTab(newValue)}
              sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab label="All Tokens" value="all" />
              <Tab label="Email" value="email" />
              <Tab label="Phone" value="phone" />
              <Tab label="Message Text" value="message_text" />
            </Tabs>

            <DataGridPro
              rows={filteredTokens}
              columns={columns}
              loading={loading}
              disableRowSelectionOnClick
              sx={{ height: 'calc(100% - 105px)', ...reportSx }}
              initialState={{
                sorting: {
                  sortModel: [{ field: 'createdAt', sort: 'desc' }],
                },
              }}
            />
          </Paper>
        )}
      </FullScreen>

      {editingToken && (
        <EditFraudTokenDialog
          open={true}
          onClose={handleCloseEditDialog}
          token={editingToken}
        />
      )}

      <AddFraudTokenDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
      />

      <FraudTokenDetailsDialog
        open={detailsDialogOpen}
        tokenId={selectedTokenId}
        onClose={() => {
          setDetailsDialogOpen(false);
          setSelectedTokenId(null);
        }}
      />
    </>
  );
};

export default FraudTokensTable;
